import { useState } from 'react';
import { useToast } from '../../../../hooks/useToast';
import { formatVideoResolutionLabel, useGenerationState } from '../../state/useGenerationState';
import { isMotionBgmPending, isMotionScene, isStillImageScene } from '../../../../features/scenarios/productionMode';
import { MOTION_SCENE_REGENERATE_AFTER_WHOLE_NOTE, isMotionEngineScenario, motionSyncNoticeText, readMotionWhole } from '../motionScenario';
import { MotionModelSelect } from '../../common/MotionModelSelect';
import { MotionQualitySelect } from '../../common/MotionQualitySelect';
import { voiceVideoStaleNotice } from '../voiceLines';
import { ConfirmDialog } from '../../../common/ConfirmDialog';
import {
  isStoryboardTextDrivenModel,
  resolveLinkPreviousMode,
  isLinkPreviousV2vReady,
  LINK_PREVIOUS_MODE_FRAME_ANCHOR_V2V,
  LINK_PREVIOUS_MODE_V2V_ONLY,
  findPreviousVideoLastFrame,
  findNearestPrecedingSceneKey,
  isLinkPreviousSourceStale,
  listPreviousVideoSources,
  facePrivacyItemLabel,
  deriveSceneInputFamily,
  resolveUsableVideoModel,
} from '../utils';
import { useGenerationModels } from '../../../../features/generation-models/hooks';
import { Tooltip } from '../../../common/Tooltip';
import { ToggleSwitch } from '../../../common/ToggleSwitch';
import { HelpTooltip } from '../../../common/HelpTooltip';
import { ClickDropdown, HoverDropdownItem } from '../../../common/HoverDropdown';
import { DurationSelect, DurationStepper } from '../DurationSelect';
import { resolveSupportedDurations, roundToSupportedDuration } from '../hooks/useAutoVideoCardSegmentation';
import { VideoHistorySelector } from '../VideoHistorySelector';
import styles from '../ScenarioGenerator.module.css';
import { useSceneStateContext } from '../hooks/SceneStateContext';
import { useSceneHandlers, type SceneHandlersContextValue } from '../hooks/SceneHandlersContext';
import { useMaterialLookup } from './useMaterialLookup';
import { STORAGE_KEY_STORYBOARD_INCLUDE_BGM } from '../constants';
import { isLyricsBgmPending, isLyricsModeScenario } from '../hooks/useBgmEditor';
import type { SceneItemProps } from '../SceneItem';
import { ThreeDotsIcon } from '../../../common/selectIcons';

type SceneVideoBoxProps = Pick<SceneItemProps,
  | 'scenario' | 'scene' | 'sceneKey' | 'videoBoxDisabledReason'
  | 'videoCardIndex' | 'videoCardCount' | 'videoCardOrders' | 'onRemoveVideoCard'
> & {
  // SceneItem 側で計算した派生値 (タイムライン/クレジット計算と共有)
  blockFirstFrameInput: boolean;
  // 「延長」(前の動画とつなげる) が実効 ON か (docs/features/
  // link-previous-scene-continuity-default-plan.md §9 step-1 項目3・4)。frame モードでは
  // フレームなしで動画生成できる (faceSafe の blockFirstFrameInput と同じ扱い)。
  frameLinkActive: boolean;
  effectiveVideoModel: string;
  effectiveResolution: string;
  sceneCredits: number;
  isSceneCreditInsufficient: boolean;
  sceneSettings: ReturnType<SceneHandlersContextValue['getSceneVideoSettings']>;
  // 「尺」ステッパー (下の DurationStepper) の選択肢。ストーリーボードモードでは
  // SceneItem の storyboardDurationOptions (総尺連動あり) を、frame/reference では
  // モデルの対応尺をそのまま並べた nonStoryboardDurationOptions を渡す (名前は歴史的経緯で
  // storyboard 由来だが、現在は全モードで使う)。
  storyboardDurationOptions: { value: number; label: string }[] | null;
  // 「特定シーンだけ生成」: 開始〜終了シーンの合計尺と丸めた尺を返す (SceneItem 側で計算)。
  getStoryboardRangeDuration: (startOrder: number, endOrder: number) => { totalSeconds: number; duration: number };
};

/**
 * Step 2「動画」生成ボックス。通常はシーンカード内、ストーリーボードモードでは
 * StoryboardCandidates 内に描画される (SceneItem の videoBoxOnly)。ストーリーボードの
 * 動画カードは「動画を追加」で複数になり得て、各カードは担当範囲の開始シーン
 * (アンカーシーン) を scene として受け取る。
 */
export function SceneVideoBox({
  scenario,
  scene,
  sceneKey: key,
  videoBoxDisabledReason,
  blockFirstFrameInput,
  frameLinkActive,
  effectiveVideoModel,
  effectiveResolution,
  sceneCredits,
  isSceneCreditInsufficient,
  sceneSettings,
  storyboardDurationOptions,
  getStoryboardRangeDuration,
  videoCardIndex,
  videoCardCount,
  videoCardOrders,
  onRemoveVideoCard,
}: SceneVideoBoxProps) {
  const {
    expandedVideos,
    setExpandedVideos,
    downloadingMaterials,
    fetchMaterialById,
    setPreviewModalMaterial,
    handleGenerateSceneVideo,
    handleSelectVideo,
    handleToggleVideoExcluded,
    handleDownloadMaterial,
    heroImageUrl,
    asin,
    manualProductId,
    includeCastingSheet,
    showCastingSheetToggle,
    onIncludeCastingSheetChange,
    isStorageExceeded,
    openPurchaseModal,
    openVideoModelDropdown,
    updateSceneVideoSettings,
    getSceneVideoSettings,
    videoSettings,
    videoGenerationMode,
    videoPlayButtonRefs,
    pendingContinuityScenes,
    motionSyncStatus,
  } = useSceneHandlers();
  const { findLoadedMaterial } = useMaterialLookup();

  // テキスト駆動モデル (DB フラグ。例: Seedance 2.5) はテキスト駆動 (絵コンテ
  // 画像を参照に送らず、シーンテキストを正本に生成する。backend f67e942b。イラスト/
  // 写真調・映像スタイルの別を問わない) のため、ストーリーボード未生成・未選択でも
  // 動画を生成できる。
  // videoBoxDisabledReason (「まずはストーリーボードを生成・選択してください」等) は
  // このカードには適用しない。カードごとにモデルが異なり得るため、カードの実効モデルで
  // 判定する。
  const { data: allVideoModels } = useGenerationModels('video');
  // シーンの入力系統 (P2 step 3。videoGenerationMode (シナリオ単位) の代わりにシーン単位で
  // 判定する。docs/features/scene-location-and-production-mode-plan.md §3.1)。storyboard
  // 判定 (production_mode 由来でシナリオ全体一律) は videoGenerationMode のままでよい。
  const sceneFamily = deriveSceneInputFamily(scenario, scene);
  const storyboardOptional = videoGenerationMode === 'storyboard'
    && isStoryboardTextDrivenModel(effectiveVideoModel, allVideoModels);
  const boxDisabledReason = storyboardOptional ? undefined : videoBoxDisabledReason;
  const toast = useToast();
  const {
    scenePreviewMaterials,
    sceneVideoLoading, sceneVideoGenerated, sceneVideoMaterialId,
    sceneVideoError, setSceneVideoError,
    playedVideos, setPlayedVideos,
    sceneLastFrames,
  } = useSceneStateContext();
  // Seedance 実在人物フィルタの原因素材 (useVideoHandlers がジョブ失敗時にセット)。
  // このカードのシーン key と一致するときだけ原因バナーを出す。
  const {
    facePrivacyFlagged,
    setIncludeStoryboardLogo,
  } = useGenerationState();
  const facePrivacyItems = (facePrivacyFlagged && facePrivacyFlagged.sceneKey === key)
    ? facePrivacyFlagged.items
    : null;

  // 三点メニューから開く「追加指示プロンプト」フォームの開閉。SceneVideoBox は
  // シーン 1 件につき 1 インスタンスなのでローカル state で十分。
  const [isExtraPromptOpen, setIsExtraPromptOpen] = useState(false);
  // 全編を生成済みのシナリオ (Phase 2f §10.6) で、このシーンだけモーションを作り直すときの確認
  const [wholeRegenerateConfirmOpen, setWholeRegenerateConfirmOpen] = useState(false);
  // 「特定シーンだけ生成」フォームの開閉 (ストーリーボードモードのみ)。開閉と有効/無効は
  // 常に一致するため、正本は設定 (sceneRangeEnabled) に一本化する。SceneItem 側の
  // マウント時復元 (保存済み範囲 / 追加カードの既定範囲) でも自動で開く。
  const isSceneRangeOpen = videoGenerationMode === 'storyboard' && !!sceneSettings.sceneRangeEnabled;

  // ストーリーボードモードで音声ありのとき表示する「BGMおまかせ／なし」。最終状態は localStorage に
  // 記録し、次回以降の既定にする (未保存時は「BGMなし」。保存値が '1' のときだけ「BGMおまかせ」)。
  // 歌モノシナリオ (bgm_mode==='lyrics') は歌が BGM になるため既定「BGMなし」。この既定は
  // localStorage を読まず、歌モノで切り替えても localStorage には書かない (通常シナリオの
  // 既定を汚さない)。サーバー側も歌モノでは include_bgm に関わらず BGM を抑止する (§5.3)。
  const isLyricsScenario = isLyricsModeScenario(scenario);
  const [includeBgm, setIncludeBgm] = useState<boolean>(() => {
    if (isLyricsScenario) return false;
    try {
      return localStorage.getItem(STORAGE_KEY_STORYBOARD_INCLUDE_BGM) === '1';
    } catch {
      return false;
    }
  });
  const handleIncludeBgmChange = (checked: boolean) => {
    setIncludeBgm(checked);
    if (isLyricsScenario) return;
    try {
      localStorage.setItem(STORAGE_KEY_STORYBOARD_INCLUDE_BGM, checked ? '1' : '0');
    } catch {
      /* localStorage 不可環境では永続化しない */
    }
  };

  // 「前の動画と繋げる」(docs/features/scenario-previous-video-link-plan.md・
  // link-previous-video-multi-model-plan.md §5.1・§5.5):
  // モデル別つなぎ方式 (link_previous_mode) が解決できるとき、前の動画カードを
  // 参照に合流させるトグルを表示する。'extend' (Gemini Omni・将来対応) もトグル自体は
  // 表示するが、参照元ドロップダウン・フレーム有無トグルは出さない (§5.5)。
  // - 先行する本編シーンが無いカード (カード1/シーン1) では出さない
  // - 前の動画が未生成 (frame_anchor_v2v はフレーム情報未取得も含む) の間は非活性 +
  //   カスタムツールチップ
  // frame モードは「延長」が実効 ON のときだけ判定に入れる (トグル UI は出さないが、
  // 前の動画未生成の間の生成ボタン非活性ゲートを storyboard/reference と共通化するため)。
  const linkPrevMode = (
    sceneFamily === 'storyboard' || sceneFamily === 'reference'
    || (sceneFamily === 'frame' && frameLinkActive)
  )
    ? resolveLinkPreviousMode(effectiveVideoModel, allVideoModels)
    : null;
  // 「延長元が更新された」バッジ (P2 step 6-3・§3.4)。自シーンの生成済み動画の
  // snapshot (generation_metadata.previous_video_source_*) と、現在の延長元シーンの
  // video_material_id を突き合わせる。family を問わず判定する (延長系生成の
  // snapshot は family でなく link_previous_video の実際の解決結果で書かれるため)。
  const linkPrevSourceStale = isLinkPreviousSourceStale(scene, scenario.scenes || []);
  const linkPrevCapable = linkPrevMode !== null;
  const prevVideoInfo = linkPrevCapable
    ? findPreviousVideoLastFrame(scenario.id, scenario.scenes || [], scene.order, sceneVideoMaterialId)
    : null;
  // 方式選択 (ON/OFF の明示切替) はシーンの入力方式3択 (SceneFrameCarousel。P2 step 4) に
  // 一本化された。frame 家系は従来どおり (§9 step-1 項目1)、reference 家系も3択が
  // video_input_mode を書くため、この動画カード側では ON/OFF トグル自体を出さない。
  // storyboard は video_input_mode の対象外 (production_mode 由来でシナリオ全体一律) の
  // ため、従来どおりこのトグルが方式選択の唯一の UI として残る。
  const linkPrevAvailable = sceneFamily !== 'frame'
    && !!(linkPrevCapable && prevVideoInfo?.hasPrecedingScene);
  const showLinkPrevToggle = linkPrevAvailable && sceneFamily === 'storyboard';
  // reference 家系: ON/OFF トグルは出さないが、延長が実効 ON (3択で 'extend' を選択/導出
  // 済み) の間は参照元選択・フレーム除外などのサブオプションだけ表示する
  // (sceneSettings.linkPreviousVideo は getSceneVideoSettings が video_input_mode から
  // 読み出し時導出する値。§3.3.1)。
  const showLinkPrevBlock = showLinkPrevToggle
    || (linkPrevAvailable && !!sceneSettings.linkPreviousVideo);
  // フレームの解決は生成 payload 構築 (useVideoHandlers) と同じフォールバック順:
  // 生成動画の終わり絵 (scenes.video_last_frame_material_id → last_frame_material_id。
  // 生成完了時の再取得で scenes に入る) を優先し、無ければユーザーの「最後のフレーム」
  // 枠 (ローカル state の sceneLastFrames) を使う。v2v_only / extend はフレーム自体を
  // 使わないため、isLinkPreviousV2vReady が前の動画の生成済みだけで有効判定する。
  const linkPrevFrameId = prevVideoInfo?.lastFrameMaterialId || (prevVideoInfo?.prevSceneKey
    ? sceneLastFrames[prevVideoInfo.prevSceneKey]?.materialId
    : undefined);
  // 参照元 (@Video1) の候補 (§10 B案)。frame_anchor_v2v / v2v_only が対象
  // (extend は選択肢を出さず常に直前の動画を延長する。§5.5「意味の混乱自体が
  // 発生しないよう選択肢を消す」)。既定は直前の動画で、劣化していない初期世代や
  // 短い尺の動画を明示的に選べる (フレームアンカーは常に直前のラストフレームのまま)。
  // 候補が 2 つ以上あるときだけセレクトを出す (1 つなら直前と同じで選ぶ意味が無い)。
  const linkPrevSourceSelectable = linkPrevMode === LINK_PREVIOUS_MODE_FRAME_ANCHOR_V2V
    || linkPrevMode === LINK_PREVIOUS_MODE_V2V_ONLY;
  const linkPrevSources = linkPrevSourceSelectable
    ? listPreviousVideoSources(scenario.scenes || [], scene.order)
    : [];
  const linkPrevSelectedSource = linkPrevSources.find(
    (s) => s.sceneId === sceneSettings.linkPreviousSourceSceneId,
  );
  const linkPrevReady = isLinkPreviousV2vReady(
    linkPrevMode, !!prevVideoInfo?.videoGenerated, linkPrevFrameId, !!linkPrevSelectedSource,
  );
  // 参照元の表示名は動画カードの表示番号「動画N」に合わせる (ユーザー指定の形式)。
  // videoCardOrders (未生成カード込みの表示順) が正本。無い経路 (参照モード等) は
  // 本編シーン順 = カード順のフォールバックで番号を振る。
  const linkPrevCardOrders = videoCardOrders ?? (scenario.scenes || [])
    .filter((s) => !s.brand_cut_position)
    .map((s) => s.order)
    .sort((a, b) => a - b);
  const linkPrevCardLabel = (order: number): string => {
    const idx = linkPrevCardOrders.indexOf(order);
    return idx >= 0 ? `動画${idx + 1}` : `シーン${order}の動画`;
  };
  // トグル ON だが接続の準備ができていない間は、トグルではなく「動画を生成」ボタンを
  // 非活性にする (トグルは先にまとめて ON にでき、前の動画の生成完了で自動的に
  // ボタンが有効化されるワークフロー)。
  // frame モード (延長実効 ON) も同じゲートを通す: 前の動画未生成のまま生成すると
  // フレームも null 済みのため「フレームもリンクも無い」入力欠落になる。フォールバック
  // ではなくボタン非活性で止める (payload 側にも最終ガードあり)。
  // reference 家系は ON/OFF トグルを出さなくなったが (showLinkPrevToggle は storyboard
  // 限定)、生成ボタンの待機ゲート自体は従来どおり linkPrevAvailable (家系問わず) で判定する。
  const linkPrevWaitTarget = (linkPrevAvailable && !!sceneSettings.linkPreviousVideo)
    || (sceneFamily === 'frame' && frameLinkActive && linkPrevCapable);
  // 延長元の前提未充足時の preflight (docs/features/
  // scene-location-and-production-mode-plan.md §3.4)。前の動画が「まだ無い」場合の
  // うち、延長元シーンの動画がいま生成中かどうかで文言を分ける。§3.4 当初案の
  // 「AIおまかせで生成」専用ボタンは、3択セグメント UI (SceneFrameCarousel) で
  // 同じ切り替えが直接できるため設けない (2026-08-31 ユーザー判断で撤去)。
  // ツールチップで3択への誘導だけ行う。
  const linkPrevNearestSceneKey = linkPrevWaitTarget
    ? findNearestPrecedingSceneKey(scenario.id, scenario.scenes || [], scene.order)
    : null;
  const linkPrevSourceGenerating = !!(
    linkPrevWaitTarget
    && !prevVideoInfo?.videoGenerated
    && linkPrevNearestSceneKey
    && sceneVideoLoading[linkPrevNearestSceneKey]
  );
  const linkPrevWaitReason = (linkPrevWaitTarget && !linkPrevReady)
    ? (linkPrevSourceGenerating
      ? '前の動画を生成中です。完了後に延長できます'
      : prevVideoInfo?.videoGenerated
        ? '前の動画のフレーム情報がまだ取得できていません。しばらく待つか、前の動画を再生成してください'
        : '前の動画がまだ生成されていません。入力方式を「AIおまかせ」に切り替えると単独で生成できます')
    : undefined;
  // 歌モノ (計画 md §5.5.1): 歌 (BGM) が採用されるまでストーリーボード動画の生成を待つ。
  // BGM 生成が歌の実測でコマ尺を書き戻し、動画カードの尺・カード内の配分がそれに追随するため。
  // テキスト駆動モデルの storyboardOptional バイパスには巻き込まない (カードローカルの別変数)。
  const lyricsBgmWaitReason = videoGenerationMode === 'storyboard' && isLyricsBgmPending(scenario)
    ? '歌モノは先に BGM (歌) を生成してください。歌のタイミングに合わせてシーンの尺を決めてから動画を生成します'
    : undefined;
  // 「静止画をそのまま使う」シーンは動画を生成しない (静止画を余白付きで表示する。動画素材をドロップ / テイク選択で
  // 紐付ければその動画を余白付きでそのまま使う)。入力方式を切り替えれば従来どおり生成できるので、
  // ボタンは非活性 + 理由をツールチップで示す。
  const isStillScene = isStillImageScene(scene);
  const stillImageReason = isStillScene
    ? 'このシーンは「静止画／スクショ動画」です (動画 AI は使いません。スクリーンショットからスクロール動画を作るか、動画素材をドロップするとその動画を使います)。動画 AI で生成するには入力方式を切り替えてください'
    : undefined;
  // モーショングラフィックスのシーン (docs/features/brand-cut-motion-graphics-plan.md §10.4): 動画 AI ではなく定額の
  // コード生成。フレーム・商品画像・動画モデルは不要。BGM 未生成でも生成できる (拍なしで書く・§15.3)。音先行 (§15.1) は
  // 注意文で促すだけにし、ゲートにはしない (2026-09-28 決定: 生成直後は「採用しない」が出せず閉じ込めるため)。
  // 後から BGM を付けると同期の鮮度判定が「BGM と同期せずに作った」で再生成を促す。
  // 絵コンテでは 'motion' が「担当カードの動画の区間を上書きする」意味 (§10.13) で、この動画カードの生成は
  // 常に絵コンテ動画 (StoryboardMotionCard が上書きの生成を別に持つ) なので false
  const isMotion = isMotionScene(scene) && videoGenerationMode !== 'storyboard';
  // 全編モーションのシナリオ (映像スタイル「モーショングラフィックス」) か。実写のシナリオで一部のシーンだけモーションにした
  // 混在の形は false で、シーン単位の「モーションを生成」が唯一の生成手段
  const wholeMotionScenario = isMotionEngineScenario(scenario);
  // 「動画」エキスパンドの開閉。全編モーションのシナリオは BGM → ナレーション → 動画の順に作るため既定で畳む
  const isVideoExpanded = expandedVideos[key] ?? !wholeMotionScenario;
  // 全編モーションのシナリオでは全編を生成する前はシーン単位のボタンを出さない (BGM の注意は MotionScenarioPanel 側が出す)
  const motionBgmHint = isMotion && isMotionBgmPending(scenario) && !sceneVideoGenerated[key] && (!wholeMotionScenario || !!readMotionWhole(scenario))
    ? 'BGM が未生成です。先に BGM を作ると、その拍に合わせたモーションになります (BGM なしでも生成できます)'
    : undefined;
  // 生成済みモーションが今の BGM / 開始位置とずれている (§15.4 の鮮度・判定は backend)。ボタンは無効化せず再生成を促す
  const motionSync = isMotion && scene.id ? motionSyncStatus?.[scenario.id]?.[scene.id] : undefined;
  // 全編モーションの計画が古い (「計画が古い: …」・§10.5) ときは backend の文言をそのまま出す
  const motionSyncStaleNotice = motionSyncNoticeText(motionSync);
  // 声が映像の生成時から変わった (声の台本・§10.7.4): 「映像の再生成が必要 (声が変わった)」
  const motionVoiceStaleNotice = voiceVideoStaleNotice(motionSync?.voice);
  // sceneCredits は SceneItem が定額 (credit_rate_config) から引く。未設定 (0) なら backend が 503 で受け付けないので送らせない
  const motionRateMissingReason = isMotion && sceneCredits <= 0
    ? 'モーショングラフィックスの料金が設定されていないため、現在ご利用いただけません'
    : undefined;
  // オプション行に他のトグル (BGM / 人物画像) が無く「前の動画とつなげる」ブロックだけの
  // 場合、三点メニューを独立行にせずトグル (またはサブオプション) と同じ行の右端に
  // 寄せる (空行の節約)。
  const linkPrevInlineWithMenu = showLinkPrevBlock
    && videoGenerationMode !== 'storyboard'
    && !showCastingSheetToggle;

  // 三点メニュー項目の左に出すチェックマーク。表示中(=フォームを開いている)項目だけ
  // チェックを見せ、非表示の項目はチェックを消す (位置揃えのため枠は常に確保)。
  const checkIcon = (active: boolean) => (
    <svg
      width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ opacity: active ? 1 : 0 }}
    >
      <path d="M5 12l5 5L20 7" />
    </svg>
  );

  // 「特定シーンだけ生成」で選べるシーン。本編動画の範囲なのでブランドカット
  // (タイトル/エンドカード) は除外し、order 昇順に並べる。
  const rangeScenes = (scenario.scenes || [])
    .filter((s) => !s.brand_cut_position)
    .slice()
    .sort((a, b) => a.order - b.order);
  const rangeMinOrder = rangeScenes[0]?.order;
  const rangeMaxOrder = rangeScenes[rangeScenes.length - 1]?.order;
  const sceneRangeStart = sceneSettings.sceneRangeStart ?? rangeMinOrder;
  const sceneRangeEnd = sceneSettings.sceneRangeEnd ?? rangeMaxOrder;

  // 範囲を適用 (有効化 or 開始/終了変更) する。終了 < 開始 にならないようクランプし、
  // 合計尺をカバーする丸め尺を「尺」に自動セットする。
  const applySceneRange = (nextStart: number, nextEnd: number, opts?: { syncNeighbors?: boolean }) => {
    const start = nextStart;
    const end = Math.max(nextStart, nextEnd);
    const { duration } = getStoryboardRangeDuration(start, end);
    updateSceneVideoSettings(key, {
      sceneRangeEnabled: true,
      sceneRangeStart: start,
      sceneRangeEnd: end,
      duration,
    });
    if (opts?.syncNeighbors) syncNeighborCardRanges(start, end);
  };

  // 隣接する動画カードの範囲を追従させる。動画カードは本編シーンを連続して分担する
  // 前提なので、このカードの終了シーンを変えたら次のカードの開始シーンをその直後へ、
  // 開始シーンを変えたら前のカードの終了シーンをその直前へ寄せる。寄せた結果その
  // カードの反対側の境界も動いた (開始 > 終了になった) 場合だけ、さらに隣へ連鎖する。
  // 範囲未設定のカードは「アンカー〜最終シーン」を現在の範囲とみなし、有効化して書き込む。
  const syncNeighborCardRanges = (start: number, end: number) => {
    if (!videoCardOrders || videoCardIndex == null) return;
    const selfIdx = videoCardIndex - 1;
    if (videoCardOrders[selfIdx] !== scene.order) return;
    const orders = rangeScenes.map((s) => s.order);
    const nextOrderAfter = (o: number) => orders.find((x) => x > o);
    const prevOrderBefore = (o: number) => [...orders].reverse().find((x) => x < o);
    const cardKeyOf = (order: number) => `${scenario.id}-scene-${order}`;
    const currentRangeOf = (order: number) => {
      const st = getSceneVideoSettings(cardKeyOf(order));
      return {
        start: Number(st.sceneRangeStart ?? order),
        end: Number(st.sceneRangeEnd ?? rangeMaxOrder ?? order),
      };
    };
    const applyTo = (order: number, s: number, e: number) => {
      const cardKey = cardKeyOf(order);
      const modelCode = resolveUsableVideoModel(getSceneVideoSettings(cardKey).videoModel, (videoSettings as { videoModel?: string }).videoModel);
      const model = (allVideoModels ?? []).find((m) => m.model_code === modelCode);
      const { totalSeconds } = getStoryboardRangeDuration(s, e);
      updateSceneVideoSettings(cardKey, {
        sceneRangeEnabled: true,
        sceneRangeStart: s,
        sceneRangeEnd: e,
        duration: roundToSupportedDuration(totalSeconds, resolveSupportedDurations(model)),
      });
    };

    // 後続カード: 開始をこのカードの終了の直後へ
    let boundary = end;
    for (let i = selfIdx + 1; i < videoCardOrders.length; i += 1) {
      const wantStart = nextOrderAfter(boundary);
      if (wantStart == null) break;
      const cur = currentRangeOf(videoCardOrders[i]);
      if (cur.start === wantStart) break;
      const newEnd = Math.max(wantStart, cur.end);
      applyTo(videoCardOrders[i], wantStart, newEnd);
      if (newEnd === cur.end) break;
      boundary = newEnd;
    }
    // 先行カード: 終了をこのカードの開始の直前へ
    boundary = start;
    for (let i = selfIdx - 1; i >= 0; i -= 1) {
      const wantEnd = prevOrderBefore(boundary);
      if (wantEnd == null) break;
      const cur = currentRangeOf(videoCardOrders[i]);
      if (cur.end === wantEnd) break;
      const newStart = Math.min(wantEnd, cur.start);
      applyTo(videoCardOrders[i], newStart, wantEnd);
      if (newStart === cur.start) break;
      boundary = newStart;
    }
  };

  // 生成ボタン (「モーションを生成 / 再生成」「動画を生成 / 再生成」+ モデル選択)。通常はエキスパンドの見出し行の右に置くが、
  // 全編モーションのシナリオのモーションシーンでは見出し行に出さず、エキスパンドを開いた中に置く (利用者指示 2026-09-29:
  // 全編は「動画を生成」で作るので、各シーンの作り直しは隠しておく)
  const videoButtonInExpand = isMotion && wholeMotionScenario && !scene.brand_cut_position;
  const videoButtonEl = (() => {
                                        // 全編モーションのシナリオの本編シーンは「全編を生成」(MotionScenarioPanel) が生成の主ルート。
                                        // シーン単位のボタンは全編を生成した後 (作り直し用) にだけ出す。初回の全編の生成中も出さない
                                        // (生成中の表示はパネルの「動画を生成中…」に任せる)。
                                        // 実写のシナリオで一部のシーンだけモーションにした混在の形には「全編を生成」が無いので従来どおり出す
                                        if (isMotion && wholeMotionScenario && !scene.brand_cut_position && !readMotionWhole(scenario)) return null;
                                        // 参照画像モード・ストーリーボードモードや、Seedance + フレームモード + 顔含み のシーン1
                                        // (= blockFirstFrameInput) では first_frame なしで動画生成できるため、hasFirstFrame を
                                        // true にして disabled / 「まずは最初のフレームを指定してください」ツールチップを回避する。
                                        const hasFirstFrame = sceneFamily === 'reference' || sceneFamily === 'storyboard' || blockFirstFrameInput || frameLinkActive || !!(scene.image_url || (scenePreviewMaterials[key] || []).some((m: any) => m?.source_url || m?.sourceUrl || m?.preview_url || m?.previewUrl));
                                        const VIDEO_MODEL_SHORT_LABELS: Record<string, string> = {
                                          'veo-3.1': '高品質',
                                          'veo-3.1-fast': 'スピード',
                                          'veo-3.1-lite': 'ライト',
                                        };
                                        const videoButtonGroup = (
                                      <div className={`${styles.videoGenerateGroup} ${sceneVideoLoading[key] ? 'ai-loading-active' : ''}`}>
                                        <button
                                          type="button"
                                          className={styles.sceneVideoButton}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            // 参照画像モードは first_frame / last_frame を送らない (a9404171) ため、
                                            // 場面継続シーンでも前シーンの動画完了を待つ必要はない (生成結果に影響しない)。
                                            setSceneVideoError((prev) => {
                                              const next = { ...prev };
                                              delete next[key];
                                              return next;
                                            });
                                            // 全編を生成済みのシナリオで生成済みのモーションを作り直すときは、一貫性の注意を確認してから
                                            if (isMotion && sceneVideoGenerated[key] && readMotionWhole(scenario)) {
                                              setWholeRegenerateConfirmOpen(true);
                                              return;
                                            }
                                            // 再生成時は未再生パルスを停止
                                            setPlayedVideos(prev => new Set(prev).add(key));
                                            handleGenerateSceneVideo(scenario, scene);
                                          }}
                                          disabled={
                                            sceneVideoLoading[key]
                                            || (!isMotion && !heroImageUrl && !asin && !manualProductId)
                                            || isSceneCreditInsufficient
                                            || isStorageExceeded
                                            || (!isMotion && !hasFirstFrame)
                                            || !!boxDisabledReason
                                            || !!linkPrevWaitReason
                                            || !!lyricsBgmWaitReason
                                            || !!stillImageReason
                                            || !!motionRateMissingReason
                                            || pendingContinuityScenes.has(key)
                                          }
                                          {...(!boxDisabledReason && (hasFirstFrame || isMotion) && !linkPrevWaitReason && !lyricsBgmWaitReason && !stillImageReason && !motionRateMissingReason ? { 'data-tooltip': `${sceneCredits}ポイントを消費します`, 'data-tooltip-delay': '' } : {})}
                                        >
                                          {sceneVideoLoading[key] ? (isMotion ? 'モーション生成中…' : '動画生成中…') : (
                                            <>
                                              {isMotion
                                                ? ((wholeMotionScenario || sceneVideoGenerated[key] || motionSyncStaleNotice || motionVoiceStaleNotice) ? 'モーションを再生成' : 'モーションを生成')
                                                : (sceneVideoGenerated[key] ? '動画を再生成' : '動画を生成')}
                                              <span className={styles.creditBadge}>{sceneCredits}</span>
                                            </>
                                          )}
                                        </button>
                                        {!isMotion && <div className={styles.videoModelDropdown}>
                                          <button
                                            type="button"
                                            className={styles.videoModelDropdownTrigger}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openVideoModelDropdown(`video-${key}`, e.currentTarget);
                                            }}
                                            disabled={sceneVideoLoading[key]}
                                            data-tooltip={`${VIDEO_MODEL_SHORT_LABELS[effectiveVideoModel] || effectiveVideoModel} ${formatVideoResolutionLabel(effectiveResolution)}`}
                                            data-tooltip-delay=""
                                          >
                                            <span className={styles.videoModelArrow}>▼</span>
                                          </button>
                                        </div>}
                                      </div>
                                        );
                                        return motionRateMissingReason ? (
                                          <Tooltip content={motionRateMissingReason}>{videoButtonGroup}</Tooltip>
                                        ) : stillImageReason ? (
                                          <Tooltip content={stillImageReason}>{videoButtonGroup}</Tooltip>
                                        ) : boxDisabledReason ? (
                                          <Tooltip content={boxDisabledReason}>{videoButtonGroup}</Tooltip>
                                        ) : (!isMotion && !hasFirstFrame) ? (
                                          <Tooltip content="まずは最初のフレームを指定してください">{videoButtonGroup}</Tooltip>
                                        ) : linkPrevWaitReason ? (
                                          <Tooltip content={linkPrevWaitReason}>{videoButtonGroup}</Tooltip>
                                        ) : lyricsBgmWaitReason ? (
                                          <Tooltip content={lyricsBgmWaitReason}>{videoButtonGroup}</Tooltip>
                                        ) : videoButtonGroup;
                                      })();

  return (
                                  <div className={styles.flowStepSection}>
                                    <div
                                      className={styles.stepHeader}
                                      onClick={() => setExpandedVideos(prev => ({ ...prev, [key]: !isVideoExpanded }))}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <div className={styles.stepTitle}>
                                        <span
                                          className={styles.expandTriangle}
                                          style={{ transform: isVideoExpanded ? 'rotate(90deg)' : 'none' }}
                                        >
                                          <svg width="12" height="12" viewBox="0 0 8 8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 1l4 3-4 3" /></svg>
                                        </span>
                                        {/* 折りたたみ時のみ、生成済み動画があればシェブロンの右に再生ボタン。 */}
                                        {!isVideoExpanded && (() => {
                                          const collapsedVideoId = sceneVideoMaterialId[key] || scene.video_material_id;
                                          if (!collapsedVideoId) return null;
                                          return (
                                            <button
                                              type="button"
                                              className={styles.collapsedPlayBtn}
                                              title="生成された動画を再生"
                                              onClick={async (e) => {
                                                e.stopPropagation();
                                                setPlayedVideos(prev => new Set(prev).add(key));
                                                const mat = findLoadedMaterial(collapsedVideoId);
                                                if (mat) {
                                                  setPreviewModalMaterial(mat);
                                                } else {
                                                  const fetched = await fetchMaterialById(collapsedVideoId);
                                                  if (fetched) setPreviewModalMaterial(fetched);
                                                  else toast.show('動画の読み込みに失敗しました。');
                                                }
                                              }}
                                            >
                                              <img src="/button_play_green.svg" alt="再生" style={{ width: '24px', height: '24px', ...(scene.video_excluded ? { filter: 'grayscale(100%) opacity(0.4)' } : {}) }} />
                                            </button>
                                          );
                                        })()}
                                        <span>
                                          {/* 動画カードが複数あるときだけ番号を出す (単独時は従来表示) */}
                                          {videoCardCount && videoCardCount > 1 && videoCardIndex
                                            ? `動画 ${videoCardIndex}`
                                            : '動画'}
                                        </span>
                                      </div>
                                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                        {/* 尺ステッパー: BGM と同様にエキスパンドタイトル行へ配置。上下ボタンで
                                            対応尺を 1 段ずつ増減する。クリックでアコーディオンが開閉しないよう
                                            stopPropagation する。 */}
                                        <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
                                          <DurationStepper
                                            value={sceneSettings.duration}
                                            onChange={(v) => updateSceneVideoSettings(key, { duration: v })}
                                            options={storyboardDurationOptions ?? undefined}
                                          />
                                        </div>
                                      {!videoButtonInExpand && videoButtonEl}
                                      </div>
                                    </div>

                                    <div className={`${styles.expandContent} ${isVideoExpanded ? styles.expanded : styles.collapsed}`}>
                                    <div className={styles.stepContent}>
                                      {/* モーションのシーンは生成ボタンがあるときだけ仕上げ方 (ライト / スタンダード / ダメ出し) の選択を添える (§10.10.1)。
                                          全編モーションのシナリオでは生成ボタンもこの行に置く */}
                                      {videoButtonEl && (videoButtonInExpand || isMotion) && (
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                                          {isMotion && <MotionModelSelect compact disabled={!!sceneVideoLoading[key]} testId={`scene-motion-model-${scene.order}`} />}
                                          {isMotion && <MotionQualitySelect route="scene" compact disabled={!!sceneVideoLoading[key]} testId={`scene-motion-quality-${scene.order}`} />}
                                          {videoButtonInExpand && videoButtonEl}
                                        </div>
                                      )}
                                      <div className={styles.stepActionRow}>
                                        {/* オプション設定 */}
                                        <div className={styles.stepOptions}>
                                          {/* 音声生成は常時オン (音声あり/なしトグルは 2026-09-14 に撤廃)。
                                              ストーリーボードモードでは「BGMおまかせ／なし」を表示。
                                              最終状態は localStorage に保持。
                                              おまかせ=動画モデルに「必要に応じて BGM を付けてよい」と
                                              指示 / なし=BGM 抑止指示 (効果音・環境音は可)。 */}
                                          {videoGenerationMode === 'storyboard' && (
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                              <ToggleSwitch
                                                checked={includeBgm}
                                                onChange={handleIncludeBgmChange}
                                                label={includeBgm ? 'BGMおまかせ' : 'BGMなし'}
                                              />
                                              <HelpTooltip ariaLabel="動画生成時のBGM設定のヘルプ">
                                                「BGMおまかせ」では、動画生成AIがシーンに合うBGM（音楽）を
                                                必要に応じて動画の音声に含めます。ただし動画と一体になるため、
                                                生成後にBGMだけを差し替えたり音量を調整したりはできず、
                                                編集時に扱いづらくなります。BGMは後からBGMセクションで
                                                別途生成・追加できるので、通常は「BGMなし」がおすすめです。
                                                「BGMなし」でも効果音・環境音は生成されます。
                                              </HelpTooltip>
                                            </div>
                                          )}
                                          {/* キャスティング選択 + 参照/ストーリーボードモード時:
                                              動画生成の参照に人物画像(キャラクターシート)を含めるトグル。
                                              キャラクター俳優には適用されないため、フルキャストに
                                              人間俳優が1人もいない場合は showCastingSheetToggle が
                                              false になり非表示になる (計画書
                                              character-casting-consistency-plan.md 修正3-b)。 */}
                                          {showCastingSheetToggle && (
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                              <ToggleSwitch
                                                checked={includeCastingSheet}
                                                onChange={(checked) => onIncludeCastingSheetChange(checked)}
                                                label="人物画像"
                                              />
                                              <HelpTooltip ariaLabel="人物画像（キャラクターシート）参照のヘルプ">
                                                この設定では人物写真のみ除外します。動画生成モデルによっては、
                                                参照に人物写真を含めると人物が弾かれて生成に失敗することが
                                                あるため、その場合はオフにしてください。キャラクター画像は、
                                                安全性検査およびモデルの参照画像上限の範囲内で添付されます
                                                （この設定の対象外です）。
                                              </HelpTooltip>
                                            </div>
                                          )}
                                          {/* その他オプションの三点メニュー (1行目=BGM の行の右端に寄せる)。
                                              「追加指示プロンプト」を選ぶと下にフォームが展開する。
                                              linkPrevInlineWithMenu のときは「前の動画とつなげる」
                                              トグルと同じ行になるため、トグル1行目に揃うよう上寄せ。 */}
                                          <div style={{ marginLeft: 'auto', ...(linkPrevInlineWithMenu ? { alignSelf: 'flex-start' } : {}) }}>
                                          <ClickDropdown
                                            position="bottom"
                                            align="right"
                                            minWidth="200px"
                                            usePortal
                                            trigger={
                                              <button
                                                type="button"
                                                className={styles.sceneMenuButton}
                                                title="その他のオプション"
                                                aria-label="その他のオプション"
                                              >
                                                <ThreeDotsIcon vertical />
                                              </button>
                                            }
                                          >
                                            {videoGenerationMode === 'storyboard' && rangeScenes.length > 0 && (
                                              <HoverDropdownItem
                                                onClick={() => {
                                                  // 表示した時点で有効化。閉じたら無効化する。
                                                  if (isSceneRangeOpen) {
                                                    updateSceneVideoSettings(key, { sceneRangeEnabled: false });
                                                  } else {
                                                    applySceneRange(sceneRangeStart ?? rangeMinOrder, sceneRangeEnd ?? rangeMaxOrder);
                                                  }
                                                }}
                                                icon={checkIcon(isSceneRangeOpen)}
                                              >
                                                特定シーンだけ生成
                                              </HoverDropdownItem>
                                            )}
                                            <HoverDropdownItem
                                              onClick={() => setIsExtraPromptOpen((o) => !o)}
                                              icon={checkIcon(isExtraPromptOpen)}
                                            >
                                              動画生成の追加プロンプト
                                            </HoverDropdownItem>
                                            {onRemoveVideoCard && (
                                              <HoverDropdownItem
                                                onClick={onRemoveVideoCard}
                                                icon={<img src="/bin_1.svg" alt="" style={{ width: '14px', height: '14px' }} />}
                                              >
                                                {videoCardIndex != null ? `動画${videoCardIndex}セクションを削除` : 'この動画セクションを削除'}
                                              </HoverDropdownItem>
                                            )}
                                          </ClickDropdown>
                                          </div>
                                          {/* 「前の動画と繋げる」: つなぎ方式が解決できるモデルのみ表示
                                              (frame_anchor_v2v=Seedance 2.5系 / v2v_only=MiniMax H3 /
                                              extend=Gemini Omni。link-previous-video-multi-model
                                              -plan.md §5.5)。ON/OFF の明示切替は storyboard 限定
                                              (showLinkPrevToggle。P2 step 4: reference 家系はシーンの
                                              入力方式3択 (SceneFrameCarousel) に一本化し、ここでは延長が
                                              実効 ON の間だけ参照元選択等のサブオプションを出す)。トグルは
                                              前の動画が未生成でも ON にでき、その間は「動画を生成」ボタン側を
                                              非活性にする (linkPrevWaitReason)。 */}
                                          {showLinkPrevBlock && (
                                            <div style={{
                                              display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6,
                                              // 他のトグルが無い場合は独立行にせず、order:-1 で三点メニュー
                                              // (DOM 上は前) より左に置き同じ行へ収める。
                                              ...(linkPrevInlineWithMenu
                                                ? { order: -1, flex: '1 1 auto', minWidth: 0 }
                                                : { flexBasis: '100%' }),
                                            }}>
                                              {showLinkPrevToggle ? (
                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                <ToggleSwitch
                                                  checked={!!sceneSettings.linkPreviousVideo}
                                                  onChange={(checked) => updateSceneVideoSettings(key, { linkPreviousVideo: checked })}
                                                  label={sceneSettings.linkPreviousVideo ? '前の動画とつなげる' : '前の動画とつなげない'}
                                                />
                                                <HelpTooltip ariaLabel="前の動画とつなげる設定のヘルプ">
                                                  前の動画のラストフレームを最初のフレームの手本として参照に加え、
                                                  服装・背景・構図が繋がるように生成します。音声は繋がりません。
                                                  対応モデルでは参照元の動画そのものも参照として使用するため、
                                                  その秒数分の生成ポイントが追加で消費されます。
                                                  参照元は既定で直前の動画ですが、画質の劣化が気になる場合は
                                                  劣化していない初期の動画を選ぶと改善が期待できます
                                                  {linkPrevMode === LINK_PREVIOUS_MODE_FRAME_ANCHOR_V2V ? (
                                                    <>（つなぎ目の構図は常に直前の動画から引き継ぎます）。</>
                                                  ) : '。'}
                                                  {linkPrevMode === LINK_PREVIOUS_MODE_FRAME_ANCHOR_V2V && (
                                                    <>
                                                      「フレームを利用しない」にすると、つなぎ目の静止画フレームを
                                                      参照から外し参照動画のみでつなげます（静止画の粗さが動画冒頭に
                                                      写り込む場合に有効ですが、つなぎ目の構図の一致は緩くなります）。
                                                    </>
                                                  )}
                                                  実写の人物の顔が含まれる場合は生成モデルの安全フィルターに
                                                  弾かれることがあります（キャラクターでの利用を推奨）。
                                                </HelpTooltip>
                                                </div>
                                              ) : (
                                                // reference 家系: ON/OFF はシーンの入力方式3択が正
                                                // (フレームカルーセル上部)。ここでは延長中である旨だけ
                                                // 控えめに添える。
                                                <span className={styles.frameLinkModeLabel}>前の動画から延長中</span>
                                              )}
                                              {!!sceneSettings.linkPreviousVideo && (linkPrevSources.length >= 2 || linkPrevMode === LINK_PREVIOUS_MODE_FRAME_ANCHOR_V2V) && (
                                                // 「特定シーンだけ生成」の範囲指定と同じガイド線付きインデント
                                                <div className={styles.optionFormBody} style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                                                  {/* 参照元 (@Video1) の選択 (§10 B案)。frame_anchor_v2v /
                                                      v2v_only のみ (extend は常に直前を延長するため出さない。
                                                      §5.5)。0 = 直前の動画 (既定)。候補はシーン order を
                                                      value にし、保存時に scene.id へ変換する。 */}
                                                  {linkPrevSources.length >= 2 && (
                                                    <DurationSelect
                                                      value={linkPrevSelectedSource?.order ?? 0}
                                                      onChange={(v) => {
                                                        const src = linkPrevSources.find((s) => s.order === v);
                                                        updateSceneVideoSettings(key, {
                                                          linkPreviousSourceSceneId: src ? src.sceneId : null,
                                                        });
                                                      }}
                                                      options={[
                                                        // 並び順は 動画1 → 動画n → 直前 (末尾)。既定値は「直前」(value 0)。
                                                        ...linkPrevSources.map((s) => ({
                                                          value: s.order,
                                                          // 尺は表示用に整数へ丸める (実測尺は 10.04 等の小数があり得る)
                                                          label: `${linkPrevCardLabel(s.order)}${s.durationSeconds ? `（${Math.round(s.durationSeconds)}秒）` : ''}`,
                                                          shortLabel: linkPrevCardLabel(s.order),
                                                        })),
                                                        {
                                                          value: 0,
                                                          // 展開時は直前の動画の尺も見せる (閉時は shortLabel の「直前」)
                                                          label: prevVideoInfo?.prevVideoDurationSeconds
                                                            ? `直前の動画（${Math.round(prevVideoInfo.prevVideoDurationSeconds)}秒）`
                                                            : '直前の動画',
                                                          shortLabel: '直前',
                                                        },
                                                      ]}
                                                      formatFallback={() => '直前'}
                                                    />
                                                  )}
                                                  {/* 静止画フレーム除外 (§11): frame_anchor_v2v 限定
                                                      (v2v_only / extend はそもそもフレームアンカーと相互排他、
                                                      またはフレームという概念自体が無い。§5.3・§5.4)。
                                                      圧縮フレームの画素が動画冒頭の画質を汚す場合に、参照動画
                                                      のみでつなげる。参照動画が使えない場合は BE がフレーム
                                                      アンカーへフォールバック。 */}
                                                  {linkPrevMode === LINK_PREVIOUS_MODE_FRAME_ANCHOR_V2V && (
                                                    <ToggleSwitch
                                                      checked={!sceneSettings.linkPreviousFrameExcluded}
                                                      onChange={(checked) => updateSceneVideoSettings(key, { linkPreviousFrameExcluded: !checked })}
                                                      label={sceneSettings.linkPreviousFrameExcluded ? 'フレームを利用しない' : 'フレームから開始する'}
                                                    />
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      {/* 特定シーンだけ生成 (ストーリーボードモードのみ・三点メニューから開閉)。
                                          有効にすると開始〜終了シーンの範囲を指定でき、その範囲だけを描く
                                          指示が生成プロンプトに付与される。尺は範囲の合計秒数を丸めた値が
                                          自動で「尺」にセットされる (手動変更も可能)。 */}
                                      {isSceneRangeOpen && videoGenerationMode === 'storyboard' && rangeScenes.length > 0 && (
                                        <div className={styles.extraPromptForm}>
                                          <label className={styles.extraPromptLabel}>特定シーンだけ生成</label>
                                          <div className={styles.optionFormBody}>
                                            <div className={styles.sceneRangeRow}>
                                              <span className={styles.sceneRangeSeparator}>シーン</span>
                                              <DurationSelect
                                                value={sceneRangeStart}
                                                onChange={(v) => applySceneRange(v, sceneRangeEnd ?? rangeMaxOrder, { syncNeighbors: true })}
                                                options={rangeScenes.map((s) => ({ value: s.order, label: String(s.order) }))}
                                                formatFallback={(v) => String(v)}
                                              />
                                              <span className={styles.sceneRangeSeparator}>〜</span>
                                              <DurationSelect
                                                value={sceneRangeEnd}
                                                onChange={(v) => applySceneRange(sceneRangeStart ?? rangeMinOrder, v, { syncNeighbors: true })}
                                                options={rangeScenes
                                                  .filter((s) => s.order >= (sceneRangeStart ?? rangeMinOrder))
                                                  .map((s) => ({ value: s.order, label: String(s.order) }))}
                                                formatFallback={(v) => String(v)}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      )}

                                      {/* 追加指示プロンプトのフォーム (三点メニューから開閉)。
                                          動画生成 AI への追加指示を入力する。値は scene 設定として
                                          保持し、生成 payload の additional_prompt として送る。 */}
                                      {isExtraPromptOpen && (
                                        <div className={styles.extraPromptForm}>
                                          <label className={styles.extraPromptLabel}>動画生成の追加プロンプト</label>
                                          <div className={styles.optionFormBody}>
                                            <textarea
                                              className={styles.extraPromptTextarea}
                                              value={sceneSettings.extraPrompt ?? ''}
                                              onChange={(e) => updateSceneVideoSettings(key, { extraPrompt: e.target.value })}
                                              placeholder="動画生成AIへの追加指示を入力（例: カメラをゆっくりズームインさせる）"
                                              rows={3}
                                            />
                                          </div>
                                        </div>
                                      )}

                                      {/* 動画セクション エラー・ガイダンス */}
                                      {sceneVideoError[key] && (
                                        <div style={{
                                          padding: '8px 12px',
                                          backgroundColor: 'rgba(220, 38, 38, 0.1)',
                                          border: '1px solid rgba(220, 38, 38, 0.3)',
                                          borderRadius: '4px',
                                          color: '#f87171',
                                          fontSize: '0.875rem',
                                          lineHeight: '1.5',
                                          marginTop: '4px'
                                        }}>
                                          {sceneVideoError[key]}
                                        </div>
                                      )}
                                      {/* 「延長元の動画が更新されています」バッジ (P2 step 6-3・
                                          scene-location-and-production-mode-plan.md §3.4)。
                                          再生成すれば新しい延長元とつなぎ直せることだけを伝える
                                          (前シーンの再生成で自動的に再生成はしない。従来どおり)。 */}
                                      {/* モーションの BGM 同期が古い (brand-cut-motion-graphics-plan.md §15.4・Phase 2d)。
                                          生成中は出さない (完了後の再取得で消える)。 */}
                                      {motionBgmHint && !sceneVideoLoading[key] && (
                                        <div className={styles.motionSyncStaleNotice}>
                                          {motionBgmHint}
                                        </div>
                                      )}
                                      {motionSyncStaleNotice && !sceneVideoLoading[key] && (
                                        <div className={styles.motionSyncStaleNotice}>
                                          {motionSyncStaleNotice}
                                        </div>
                                      )}
                                      {motionVoiceStaleNotice && !sceneVideoLoading[key] && (
                                        <div className={styles.motionSyncStaleNotice} data-testid="motion-voice-video-stale">
                                          {motionVoiceStaleNotice}
                                        </div>
                                      )}
                                      {linkPrevSourceStale && (
                                        <div className={styles.linkPrevSourceStaleBadge}>
                                          延長元の動画が更新されています。再生成でつなぎ直せます
                                        </div>
                                      )}
                                      {/* 実在人物フィルタの原因素材バナー: 該当画像のサムネイルと
                                          対処 (添付解除) を提示する。赤枠のチェックボックス・
                                          絵コンテカードは StoryboardCandidates 側。選択素材は
                                          選択中素材カード側 (赤枠+「覆面化する」ボタン)、商品画像は
                                          「商品画像を添付」チェック側 (赤枠) で対処を示すため、
                                          バナーには載せない */}
                                      {facePrivacyItems && (() => {
                                        const bannerItems = facePrivacyItems.filter(
                                          (it) => it.kind !== 'selected_asset' && it.kind !== 'product_tile' && it.kind !== 'product_image',
                                        );
                                        if (bannerItems.length === 0) return null;
                                        const kinds = new Set(bannerItems.map((it) => it.kind));
                                        return (
                                          <div className={styles.facePrivacyFlaggedBanner}>
                                            <div className={styles.facePrivacyFlaggedItems}>
                                              {bannerItems.map((it, i) => (
                                                <div key={i} className={styles.facePrivacyFlaggedItem}>
                                                  {it.url && !it.url.startsWith('asset://') && (
                                                    <img src={it.url} alt="" className={styles.facePrivacyFlaggedThumb} />
                                                  )}
                                                  <span>{facePrivacyItemLabel(it)}</span>
                                                </div>
                                              ))}
                                            </div>
                                            <div className={styles.facePrivacyFlaggedActions}>
                                              {kinds.has('logo') && (
                                                <button
                                                  type="button"
                                                  className={styles.facePrivacyFlaggedButton}
                                                  onClick={() => {
                                                    setIncludeStoryboardLogo(false);
                                                    toast.show('ロゴ画像の添付を解除しました。もう一度生成してください', 8000);
                                                  }}
                                                >
                                                  ロゴ添付を解除
                                                </button>
                                              )}
                                            </div>
                                            {kinds.has('storyboard') && (
                                              <div className={styles.facePrivacyFlaggedHint}>
                                                絵コンテ画像が対象の場合は、別の絵コンテを選択し直すこともできます。
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })()}
                                      {(isSceneCreditInsufficient || isStorageExceeded || (!heroImageUrl && !asin && !manualProductId)) && (
                                        <div className={styles.sceneFooterGuidance}>
                                          {(!heroImageUrl && !asin && !manualProductId) && <div>素材を選択するか商品を選択してください。</div>}
                                          {isSceneCreditInsufficient && (
                                            <div>ポイントが不足しています。<button type="button" onClick={openPurchaseModal} style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}>ポイントを購入</button>してください。</div>
                                          )}
                                          {isStorageExceeded && (
                                            <div>ストレージ容量の上限に達しました。<a href="/trash">ごみ箱</a>を空にすると空き容量を確保できます。プランのアップグレードで容量を引き上げることも可能です。<a href="/subscription">プランを確認</a></div>
                                          )}
                                        </div>
                                      )}

                                   </div>

                                      {/* 結果エリア */}
                                      <div className={styles.stepResultArea} style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr' }}>
                                        {(() => {
                                          const videoMaterialId = sceneVideoMaterialId[key] || scene.video_material_id;
                                          const hasVideo = !!videoMaterialId;
                                          const videoMaterial = findLoadedMaterial(videoMaterialId);

                                          return (
                                            <>
                                              <div style={{ justifySelf: 'start', display: 'flex', alignItems: 'center' }}>
                                                {hasVideo ? (
                                                  <button
                                                    ref={(el) => {
                                                      if (el) {
                                                        videoPlayButtonRefs.current.set(key, el);
                                                      } else {
                                                        videoPlayButtonRefs.current.delete(key);
                                                      }
                                                    }}
                                                    type="button"
                                                    onClick={async () => {
                                                      // 再生済みとしてマーク
                                                      setPlayedVideos(prev => new Set(prev).add(key));

                                                      if (!videoMaterial && videoMaterialId) {
                                                        const fetchedMaterial = await fetchMaterialById(videoMaterialId);
                                                        if (fetchedMaterial) {
                                                          setPreviewModalMaterial(fetchedMaterial);
                                                        } else {
                                                          toast.show('動画の読み込みに失敗しました。');
                                                        }
                                                      } else if (videoMaterial) {
                                                        setPreviewModalMaterial(videoMaterial);
                                                      }
                                                    }}
                                                    title={scene.video_excluded ? '採用なし（テイクを選ぶと採用されます）' : '生成された動画を再生'}
                                                    className={!playedVideos.has(key) && !scene.video_excluded ? styles.videoPlayUnplayed : undefined}
                                                    style={{
                                                      border: 'none',
                                                      background: 'transparent',
                                                      cursor: 'pointer',
                                                      padding: '0',
                                                      display: 'inline-flex',
                                                      alignItems: 'center',
                                                    }}
                                                  >
                                                    <img
                                                      src="/button_play_green.svg"
                                                      alt="再生"
                                                      style={{ width: '24px', height: '24px', ...(scene.video_excluded ? { filter: 'grayscale(100%) opacity(0.4)' } : {}) }}
                                                    />
                                                  </button>
                                                ) : (
                                                  <Tooltip content={isStillScene ? '動画素材をここにドロップすると、余白付きでそのまま使います' : 'まずは動画を生成してください'}>
                                                    <button
                                                      ref={(el) => {
                                                        if (el) {
                                                          videoPlayButtonRefs.current.set(key, el);
                                                        } else {
                                                          videoPlayButtonRefs.current.delete(key);
                                                        }
                                                      }}
                                                      type="button"
                                                      disabled
                                                      style={{
                                                        border: 'none',
                                                        background: 'transparent',
                                                        cursor: 'default',
                                                        padding: '0',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                      }}
                                                    >
                                                      <img
                                                        src="/button_play_green.svg"
                                                        alt="再生"
                                                        style={{ width: '24px', height: '24px', filter: 'grayscale(100%) opacity(0.4)' }}
                                                      />
                                                    </button>
                                                  </Tooltip>
                                                )}
                                                <VideoHistorySelector
                                                  sceneId={scene.id}
                                                  currentMaterialId={videoMaterialId}
                                                  onSelect={(materialId, durationSeconds, hasAudio) => handleSelectVideo(scene.id, materialId, key, durationSeconds, hasAudio)}
                                                  excluded={!!scene.video_excluded}
                                                  onToggleExcluded={(excluded) => handleToggleVideoExcluded(scene.id, excluded)}
                                                  videoModels={allVideoModels}
                                                />
                                              </div>
                                              <div />
                                              <div style={{ justifySelf: 'end' }}>
                                                <button
                                                  type="button"
                                                  className={`${styles.downloadButton}${sceneVideoGenerated[key] ? ` ${styles.hasVideo}` : ''}${videoMaterialId && downloadingMaterials.has(videoMaterialId) ? ` ${styles.downloading}` : ''}`}
                                                  disabled={!hasVideo || (videoMaterialId ? downloadingMaterials.has(videoMaterialId) : false)}
                                                  onClick={() => {
                                                    if (videoMaterialId) {
                                                      setPlayedVideos(prev => new Set(prev).add(key));
                                                      const namePrefix = scenario.title.replace(/[\\/:*?"<>|]/g, '_');
                                                      handleDownloadMaterial(videoMaterialId, `${namePrefix}_scene_${scene.order}_video`);
                                                    }
                                                  }}
                                                  title={videoMaterialId && downloadingMaterials.has(videoMaterialId) ? 'ダウンロード中...' : hasVideo ? '動画をダウンロード' : '動画未生成'}
                                                >
                                                  <img src="/download_bottom.svg" alt="ダウンロード" />
                                                </button>
                                              </div>
                                            </>
                                          );
                                        })()}
                                      </div>
                                   </div>
                                  <ConfirmDialog
                                    isOpen={wholeRegenerateConfirmOpen}
                                    title="このシーンのモーションを再生成"
                                    message={
                                      <>
                                        {MOTION_SCENE_REGENERATE_AFTER_WHOLE_NOTE}
                                        <br />
                                        {sceneCredits} ポイントを消費します。
                                      </>
                                    }
                                    confirmLabel="再生成する"
                                    variant="warning"
                                    onConfirm={() => {
                                      setWholeRegenerateConfirmOpen(false);
                                      setPlayedVideos(prev => new Set(prev).add(key));
                                      handleGenerateSceneVideo(scenario, scene);
                                    }}
                                    onCancel={() => setWholeRegenerateConfirmOpen(false)}
                                  />
                                </div>
  );
}
